import { useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { saveProductSearch } from "./services";
import { buildFilterGroups, datasetLabel, normalizeDirectionLabel } from "./utils";
import type { ProductSearchData, ProductSearchFilters } from "./types";

type SaveSearchButtonProps = {
  searchedState: ProductSearchData | null;
  filters?: ProductSearchFilters;
  onSaved?: () => void;
};

const pillCls = "tw-flex tw-items-center tw-gap-1 tw-w-fit tw-rounded-full tw-border tw-border-gray-300 tw-px-2.5 tw-py-0.5 tw-text-xs tw-text-gray-900";
const sectionTitleCls = "tw-text-xs tw-font-medium tw-text-gray-600";
const headingCls = "tw-text-sm tw-font-medium tw-text-gray-900";

const SaveSearchButton = ({ searchedState, filters, onSaved }: SaveSearchButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [saving, setSaving] = useState(false);

  if (!searchedState) return null;

  const category = normalizeDirectionLabel(searchedState.tradeDirection?.value);
  const filterGroups = buildFilterGroups(filters, searchedState.tradeDirection?.value);

  const handleClose = () => {
    setIsOpen(false);
    setSearchName("");
  };

  const handleSave = async () => {
    if (!searchName.trim()) {
      toast.error("Please enter a name for this search");
      return;
    }
    setSaving(true);
    try {
      const res = await saveProductSearch({
        search_name: searchName.trim(),
        search_category: category,
        trade_direction: searchedState.tradeDirection?.value,
        url: window.location.pathname + window.location.search,
        search_data: {
          search: searchedState.search,
          tradeDirection: searchedState.tradeDirection,
          datasetType: searchedState.datasetType,
          filters,
        },
      });
      if (res?.status === "success") {
        toast.success(res.message || "Search saved successfully");
        handleClose();
        onSaved?.();
      } else {
        toast.error(res?.message || "Something went wrong. Please try again later!");
      }
    } catch {
      toast.error("Something went wrong. Please try again later!");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="tw-flex tw-gap-1 tw-items-center tw-cursor-pointer" onClick={() => setIsOpen(true)}>
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#5a1ce2">
          <path d="M820-671.54v459.23Q820-182 799-161q-21 21-51.31 21H212.31Q182-140 161-161q-21-21-21-51.31v-535.38Q140-778 161-799q21-21 51.31-21h459.23L820-671.54ZM479.91-269.23q41.63 0 70.86-29.14Q580-327.51 580-369.14q0-41.63-29.14-70.86-29.14-29.23-70.77-29.23-41.63 0-70.86 29.14Q380-410.95 380-369.32q0 41.63 29.14 70.86 29.14 29.23 70.77 29.23ZM255.39-564.62h328.45v-139.99H255.39v139.99Z" />
        </svg>
        <span className="tw-text-sm tw-font-medium tw-text-primary-purple-600">Save Search</span>
      </div>

      <Modal show={isOpen} onHide={handleClose} centered animation={false} dialogClassName="tw-max-w-[620px] tw-w-full" contentClassName="!tw-rounded-[5px] !tw-border-0 !tw-shadow-[0_0_10px_rgba(0,0,0,0.3)] !tw-p-0">
        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-300 tw-px-3 tw-py-3">
          <span className="tw-text-base tw-font-medium tw-text-gray-900">Save Search</span>
          <span className="material-symbols-outlined tw-cursor-pointer tw-text-gray-900 tw-font-bold" onClick={handleClose}>
            close
          </span>
        </div>
        <div className="tw-px-4 tw-py-3">
          <div className="tw-flex tw-flex-col tw-gap-2">
            <div className="tw-text-sm tw-font-medium tw-text-gray-600">Add a name to this search</div>
            <input autoFocus type="text" placeholder="Enter name for search" value={searchName} onChange={(e) => setSearchName(e.target.value)} className="tw-h-9 tw-rounded-lg tw-border tw-border-gray-300 tw-bg-gray-50 tw-px-3 tw-text-sm focus:tw-outline-none focus:tw-border-primary-purple-500 focus:tw-bg-white" />
          </div>

          <div className="tw-flex tw-bg-gray-50 tw-rounded-lg tw-my-3">
            <div className="tw-w-1/2 tw-flex tw-flex-col tw-gap-5 tw-p-3">
              <div className={headingCls}>Searched Details</div>
              <div className="tw-flex tw-flex-col tw-gap-1">
                <div className={sectionTitleCls}>Search Category</div>
                <div className="tw-flex tw-flex-wrap tw-gap-3">
                  <span className={pillCls}>{category}</span>
                </div>
              </div>
              <div className="tw-flex tw-flex-col tw-gap-1">
                <div className={sectionTitleCls}>Search Phrase</div>
                <div className="tw-flex tw-flex-wrap tw-gap-3">
                  {searchedState.search?.length ? (
                    searchedState.search.map((item) => (
                      <span key={item.label} className={pillCls}>
                        {item.label}
                      </span>
                    ))
                  ) : (
                    <span className={pillCls}>NA</span>
                  )}
                </div>
              </div>
              {datasetLabel(searchedState.datasetType) ? (
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <div className={sectionTitleCls}>Dataset</div>
                  <div className="tw-flex tw-flex-wrap tw-gap-3">
                    <span className={pillCls}>{datasetLabel(searchedState.datasetType)}</span>
                  </div>
                </div>
              ) : null}
              <div className="tw-flex tw-flex-col tw-gap-2">
                <div className={sectionTitleCls}>Date Range</div>
                <div className="tw-flex tw-flex-wrap tw-gap-3">
                  <span className={pillCls}>{filters?.dateRange?.label || "NA"}</span>
                </div>
              </div>
            </div>
            <div className="tw-w-1/2 tw-flex tw-flex-col tw-gap-5 tw-p-3 tw-border-l tw-border-gray-200">
              <div className={headingCls}>Applied Filters</div>
              {filterGroups.length ? (
                filterGroups.map((group) => (
                  <div key={group.label} className="tw-flex tw-flex-col tw-gap-2">
                    <div className={sectionTitleCls}>{group.label}</div>
                    <div className="tw-flex tw-flex-wrap tw-gap-3">
                      {group.pills.map((pill) => (
                        <span key={pill.text} className={pillCls} data-tip={pill.tooltip || ""}>
                          {pill.text}
                        </span>
                      ))}
                      {group.overflowCount ? (
                        <span className={pillCls} data-tip={group.overflowTooltip}>
                          +{group.overflowCount}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ))
              ) : (
                <span className="tw-text-sm tw-text-gray-500">No filters have been added</span>
              )}
            </div>
          </div>
        </div>
        <div className="tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-gray-200 tw-px-4 tw-py-3">
          <button type="button" className="tw-h-9 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-px-4 tw-text-sm tw-font-medium tw-text-gray-700 tw-shadow-sm hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed" onClick={handleClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="tw-h-9 tw-rounded-lg tw-border-0 tw-bg-primary-purple-600 tw-px-4 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary-purple-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed" disabled={saving || !searchName.trim()} onClick={handleSave}>
            {saving ? "Saving..." : "Save Search"}
          </button>
        </div>
      </Modal>
      <ReactTooltip className="trademo-tooltip" backgroundColor="#8c8c8c" textColor="#FFFFFF" />
    </>
  );
};

export default SaveSearchButton;
